import axios from "axios";
import { baseUrl } from ".";
import { stripStoredUserTokenSuffix } from "./token";
import { ResultMessageProps } from "./user";

// The publishStatus value for a sold property (drives the "Sold" tag across the
// dashboard/listing surfaces). Named so the literal isn't scattered inline.
export const PUBLISH_STATUS_SOLD = "sold";

export const SOLD_PILL_CLASSES = "bg-[#DC2626] text-white";

export interface PropertiesSearch {
  page: Number;
  limit: Number;
  propertyType?: String;
  minPrice?: Number;
  maxPrice?: Number;
  minGrossYield?: Number;
  maxGrossYield?: Number;
  searchKeyword?: String;
  sortDate?: string;
  region?: string;
}

export interface Filter {
  maxGrossYield: number;
  maxPrice: number;
  minGrossYield: number;
  minPrice: number;
  minBedroom: number;
  maxBedroom: number;
  propertyType: string[];
  strategy: string[];
  regions: string[];
  towns: string[];
}
export interface Pagination {
  total: number;
  next: number;
  prev: number;
}

export interface Theme {
  color1: String;
  color2: String;
  logo: String;
  paymentAccept: Boolean;
}

export interface PropertiesExtraResult {
  results: Property[];
  filter: Filter;
  message: String;
  pagination: Pagination;
  theme: Theme;
}

export interface PropertyExtraResult {
  results: Property;
  message: String;
  theme: Theme;
}

export interface Property {
  results: any;
  region: string;
  someField: any;
  propertykeyFeatures?: string[] | null;
  strategy?: string | null;
  currentRent?: number | null;
  images?: ImagesEntity[] | null;
  sizing?: string[] | null;
  wishlist: boolean;
  rentComparable?: string[] | null;
  saleComparable?: string[] | null;
  marketComparable?: null[] | null;
  streetComparable?: null[] | null;
  benefits?: string[] | null;
  refurbishments?: string[] | null;
  nearestStation?: NearestStationEntity[] | null;
  nearestSchool?: NearestSchoolEntity[] | null;
  createdAt: string;
  updatedAt: string;
  listingViewDate?: string;
  _id: string;
  propertyId: string;
  connectedTo: string;
  propertyDescription: string;
  propertyOf: string;
  website: string;
  longitude?: null;
  latitude?: null;
  postcode: string;
  lhaRent: number;
  price: number;
  bedrooms: number;
  buildingUnits?: number;
  isListingLive: boolean;
  houseType: string;
  houseSubtype: string;
  localcontact: string;
  houseNumber: string;
  averageRent: number;
  lhaGrossYield?: null;
  lhaNetYield?: null;
  rentGrossYield: number;
  rentNetYield?: null;
  averagePriceValution?: null;
  rentPropertyMacthed?: null;
  estateAgentEmail: string;
  estateAgentPhone: string;
  estateAgentName: string;
  brandName: string;
  branchName: string;
  discountToMarket: number;
  discountLabel?: string;
  returnToMarket: number;
  listedDate: string;
  tenureType: string;
  username: string;
  userHandle: string;
  agentAddress: string;
  agentPhone: string;
  refurbishmentCost: number;
  epc: string;
  floorsqft: string;
  floodRisk: string;
  offerPrice: number;
  soldStc: boolean;
  reserved: boolean;
  publishStatus?: "available" | "withdrawn" | "paused" | "reserved" | "held" | "sold" | "inLegals";
  /** True for owner-pinned hot properties — server pins them first on page 1 of the list response. */
  isHot?: boolean;
  reservationPrice: number;
  propertyFrom: string;
  estimatedValue: number;
  legal: number;
  investment: number;
  occupancy: string;
  depositeProperty: number;
  management: number;
  brokerage: number;
  opex: number;
  mortgage: number;
  mortgageLabel: string;
  netCashflow: number;
  setupFeeValuation: number;
  annualCashflow: number;
  stampDuty: number;
  asteroomUrl?: string;
  __v: number;
}
export interface ImagesEntity {
  url: string;
  caption?: null;
  resizedImageUrls: ResizedImageUrls;
}
export interface ResizedImageUrls {
  size135x100: string;
  size476x317: string;
  size656x437: string;
}
export interface NearestStationEntity {
  station: string;
  distance: string;
}
export interface NearestSchoolEntity {
  school: string;
  distance: string;
}
interface EnquiryData {
  name: string;
  phoneNumber: string;
  email: string;
  productId: string;
  userId?: string;
  websiteName?: string;
  websiteURL?: string;
  // NEW FIELDS
  serviceOwnService?: boolean;
  calendarLinkUsed?: string | null;
  calendarCompleted?: boolean;
  appointmentDateTime?: Date;
  updateExisting?: boolean; // Flag to indicate this is an update
  // ADDITIONAL FIELDS FOR FORM MODAL
  formModalUsed?: boolean;
  formCompleted?: boolean;
  formData?: string;
}

interface EnquiryFormData {
  enquiryId: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  productId: string;
  createdAt: string;
  formModalUsed?: boolean;
  formCompleted?: boolean;
}

// Function to fetch enquiry data by ID for form prepopulation
export const fetchEnquiryData = async (enquiryId: string, token?: string): Promise<EnquiryFormData | null> => {
  try {
    if (!token || !enquiryId) {
      console.log('No token or enquiry ID available');
      return null;
    }

    // Strip the B9I9N9456 suffix from the token if present
    const jwt = token.replace("B9I9N9456", "");

    const response = await fetch(`${baseUrl}/properties/enquiries/${enquiryId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('Enquiry not found');
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    console.log('Enquiry data fetched successfully:', {
      enquiryId: result.data.enquiryId,
      hasName: !!result.data.name,
      hasEmail: !!result.data.email
    });

    return result.data;
  } catch (error) {
    console.error('Error fetching enquiry data:', error);
    return null;
  }
};

export const sendEnquiry = async (data: EnquiryData) => {
  try {
    const response = await fetch(`${baseUrl}/properties/enquiries`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${window.__TENANT__.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: data.name,
        phoneNumber: data.phoneNumber,
        email: data.email,
        productId: data.productId,
        userId: data.userId,
        websiteName: data.websiteName,
        websiteURL: data.websiteURL,
        // NEW FIELDS
        serviceOwnService: data.serviceOwnService || false,
        calendarLinkUsed: data.calendarLinkUsed || null,
        calendarCompleted: data.calendarCompleted || false,
        appointmentDateTime: data.appointmentDateTime || null,
        updateExisting: data.updateExisting || false,
        formModalUsed: data.formModalUsed || false,
        formCompleted: data.formCompleted || false,
        formData: data.formData || null,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Return both the result and the enquiryId for form prepopulation
    return {
      ...result,
      enquiryId: result.data?.enquiryId || result.enquiryId // Handle both response formats
    };
  } catch (error) {
    console.error('Error submitting enquiry:', error);
    throw error;
  }
};

export const getProperties = async (
  data: PropertiesSearch
): Promise<PropertiesExtraResult> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/properties`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    window.location.reload();
    return undefined as any;
  }
};


export const getFeaturedProperties =
  async (): Promise<PropertiesExtraResult> => {
    try {
      const response = await axios.get(
        `${baseUrl}/whitelabel/getFeaturedProperties`,
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
            Authorization: `Bearer ${window.__TENANT__.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.log(error);
      window.location.reload();
      return undefined as any;
    }
  };

export const getProperty = async (id: string, buyerToken?: string): Promise<PropertyExtraResult> => {
  const url = `${baseUrl}/whitelabel/singleProperty/${id}`;
  const baseHeaders = {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
  };
  try {
    const response = await axios.get(url, {
      headers: { ...baseHeaders, Authorization: `Bearer ${window.__TENANT__.token}` },
    });
    return response.data;
  } catch (error) {
    // A property hidden from this site's catalogue 404s for the site token, but
    // a buyer who reserved it may still view it from their reservation history.
    // Retry once with the buyer token so the server's reservation-scoped bypass
    // can apply — the common (visible) path never triggers this retry.
    const is404 = axios.isAxiosError(error) && error.response?.status === 404;
    if (buyerToken && is404) {
      try {
        const retry = await axios.get(url, {
          headers: { ...baseHeaders, Authorization: `Bearer ${stripStoredUserTokenSuffix(buyerToken)}` },
        });
        return retry.data;
      } catch {
        // Buyer retry also failed — fall through to the existing empty result.
        return undefined as any;
      }
    }
    return undefined as any;
  }
};

export const checkout = async ({ id, url, token }: any): Promise<any> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/checkout`,
      { _id: id, url: url },
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${stripStoredUserTokenSuffix(token)}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return undefined as any;
  }
};

export interface BuyerCreditBalance {
  available: boolean;
  amount: number; // summed integer minor units (pence) across available credits
  maxAmount: number; // largest single available credit — redemption fires only when this covers the fee
  currency: string;
  count: number;
}

const EMPTY_BUYER_CREDIT_BALANCE: BuyerCreditBalance = {
  available: false,
  amount: 0,
  maxAmount: 0,
  currency: "gbp",
  count: 0,
};

// The signed-in buyer's available credit balance on their site, for the "credit
// available" listing hint. Uses the buyer token (same as checkout). Returns an
// empty balance on any error / when the partner is not ramped, so the hint simply
// does not show.
export const getBuyerCreditBalance = async ({
  token,
}: {
  token: string;
}): Promise<BuyerCreditBalance> => {
  try {
    const response = await axios.get(`${baseUrl}/whitelabel/buyer-credit`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${stripStoredUserTokenSuffix(token)}`,
      },
    });
    return (response.data && response.data.data) || EMPTY_BUYER_CREDIT_BALANCE;
  } catch {
    // Fail closed and silent: the hint is purely additive, so a rollout mismatch,
    // transient error, or expired token must just hide it — never log noise on
    // every property page load.
    return EMPTY_BUYER_CREDIT_BALANCE;
  }
};

// Count-only listing activity for the social-proof overlay, from
// GET /whitelabel/property-activity/:propertyId. Uses the whitelabel SITE
// token (same tenant/auth contract as getProperty) so anonymous visitors on
// skip-login sites see the overlay too — the endpoint takes appId from the
// site token and needs no buyer identity. Fails closed to null — the overlay
// is purely additive, so a rollout mismatch or transient error must just hide
// it (mirrors getBuyerCreditBalance).
export const fetchPropertyActivity = async ({
  propertyId,
}: {
  propertyId: string;
}): Promise<import("./listingActivity").PropertyActivity | null> => {
  try {
    const response = await axios.get(`${baseUrl}/whitelabel/property-activity/${propertyId}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${window.__TENANT__.token}`,
      },
    });
    return (response.data && response.data.data) || null;
  } catch {
    return null;
  }
};

// Batch variant of fetchPropertyActivity for the /listing catalogue grid: one
// POST for a page of property ids -> a per-id activity map. Site token like
// fetchPropertyActivity, and fails closed to {} (no pills) on any error/empty
// body so a rollout mismatch or transient failure never breaks the grid.
export const fetchPropertyActivityBatch = async ({
  propertyIds,
}: {
  propertyIds: string[];
}): Promise<Record<string, import("./listingActivity").PropertyActivity>> => {
  if (propertyIds.length === 0) return {};
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/property-activity`,
      { propertyIds },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return (response.data && response.data.data) || {};
  } catch {
    return {};
  }
};

// Buyer-facing reservation history row from GET /whitelabel/my-reservations
// (tva-api buyerReservationsService). Statuses are derived server-side; refund
// wins over credit so a row never reads as both.
export type BuyerReservationStatus =
  | "reserved"
  | "cancelled_refunded"
  | "cancelled_credited"
  | "cancelled";

export interface BuyerReservationSpentOn {
  orderId: string;
  propertyId: string | null;
  propertyName: string | null;
  // false when the property no longer exists (deleted) — the listing page would
  // 404, so the dashboard shows the name without a link. A property merely
  // hidden from the catalogue stays viewable: the buyer reserved it, so
  // /singleProperty grants a reservation-scoped bypass.
  viewable: boolean;
  spentAt: string | null;
}

export interface BuyerReservationCredit {
  amount: number | null; // integer minor units (pence)
  currency: string;
  // null = unknown/degraded (ledger row missing, or partner not ramped for
  // redemption so a spendable claim is withheld server-side)
  status: "available" | "spent" | "revoked" | null;
  spentOn: BuyerReservationSpentOn | null;
}

export interface BuyerReservationProperty {
  id: string;
  name: string | null;
  address: string | null;
  image: string | null;
  price: number | null;
  // false when the property no longer exists (deleted) — the listing page would
  // 404, so the dashboard shows it without a link. A property merely hidden from
  // the catalogue stays viewable: the buyer reserved it, so /singleProperty
  // grants a reservation-scoped bypass.
  viewable: boolean;
  // true when the property has sold since this buyer reserved it
  // (publishStatus === "sold"). Optional: absent until the API ships it.
  sold?: boolean;
}

export interface BuyerReservation {
  id: string | null;
  property: BuyerReservationProperty | null;
  status: BuyerReservationStatus;
  free: boolean;
  paidWithCredit: boolean;
  feeAmount: number | null;
  source: string | null;
  reservedAt: string | null;
  cancelledAt: string | null;
  credit: BuyerReservationCredit | null;
}

// The signed-in buyer's reservation history for the /dashboard "My
// Reservations" card. Buyer-token scoped like getBuyerCreditBalance. Fails
// closed to an empty list so the card degrades to its empty state on any
// error / while the API is not yet deployed.
export const fetchBuyerReservations = async ({
  token,
}: {
  token: string;
}): Promise<BuyerReservation[]> => {
  try {
    const response = await axios.get(`${baseUrl}/whitelabel/my-reservations`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${stripStoredUserTokenSuffix(token)}`,
      },
    });
    const rows = response.data && response.data.data;
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
};

interface OrderParamsProps {
  connectedTo: string;
  paymentStatus: string;
  token: string;
}

export const orderProperty = async (
  data: OrderParamsProps
): Promise<ResultMessageProps> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/reserve`,
      {
        connectedTo: data.connectedTo,
        paymentStatus: data.paymentStatus,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token.replace("B9I9N9456", "")}`,
        },
      }
    );
    return { status: response.status, message: response.data.message };
  } catch (error: any) {
    console.log(error);
    return {
      status: error.response.status,
      message: error.response.data.message,
    };
  }
};

export interface ContactFormProps {
  email: string;
  firstname: string;
  lastname: string;
  mobilephone: string;
  message: string;
}

export const contactFormContact = async (
  data: ContactFormProps
): Promise<ResultMessageProps> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/getintouch`,
      {
        email: data.email,
        firstname: data.firstname,
        lastname: data.lastname,
        mobilephone: data.mobilephone,
        message: data.message,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return { status: response.status, message: response.data.message };
  } catch (error: any) {
    console.log(error);
    return {
      status: error.response.status,
      message: error.response.data.message,
    };
  }
};

interface MakeAnOfferProps {
  connectedTo: string;
  offer: string;
  propertyLink: string;
  token: string;
}

export const postMakeAnOffer = async ({
  connectedTo,
  offer,
  propertyLink,
  token,
}: MakeAnOfferProps): Promise<ResultMessageProps> => {
  try {
    // console.log(connectedTo, offer, propertyLink, token);
    const response = await axios.post(
      `${baseUrl}/whitelabel/makeoffer`,
      {
        connectedTo,
        offer,
        propertyLink,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.replace("B9I9N9456", "")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return undefined as any;
  }
};

export const numberFormat = (value: number) => {
  let internationalNumberFormat = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return internationalNumberFormat.format(value);
};

export interface SandboxPropertyResult extends PropertyExtraResult {
  sandboxMode: boolean;
  whitelabelTheme?: any;
}

const sandboxApiBase = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/v1`
  : baseUrl;

export const getSandboxProperty = async (token: string): Promise<SandboxPropertyResult> => {
  const response = await axios.get(
    `${sandboxApiBase}/whitelabel/sandbox/property/${token}`,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export interface NewBuildUnit {
  unit?: string;
  beds?: number;
  price?: number;
  status?: 'available' | 'sold' | 'held' | 'unknown';
  available?: boolean;
  [key: string]: any;
}

export interface NewBuildAvailabilitySheet {
  id: number;
  name: string;
  notes?: string;
  url: string;
}

export interface NewBuildFaq {
  question: string;
  answer: string;
}

export interface NewBuildPropertyItem {
  _id: string;
  title?: string;
  developer?: string;
  development?: string;
  address?: string;
  location?: string;
  hook?: string;
  headline?: string;
  highlights?: string;
  selling_points?: string[];
  faqs?: NewBuildFaq[];
  price_from?: string;
  price_to?: string;
  no_of_units?: number;
  no_of_levels?: number;
  image?: string;
  images?: { url: string; [key: string]: any }[];
  units?: NewBuildUnit[];
  availability_sheets?: NewBuildAvailabilitySheet[];
  published: boolean;
  grossYield?: number;
  purchasePrice?: number;
  averageRent?: number;
  postcode?: string;
  region?: string;
  town?: string;
  propertyType?: string;
  tenureType?: string;
  hubDirectId?: number;
  last_synced_at?: string;
  createdAt?: string;
  listingViewDate?: string;
  listingOrder?: number;
  deposite?: number;
  lettingAgentFees?: number;
  latitude?: number;
  longitude?: number;
  schools?: any[];
  stations?: any[];
  onStreetCompare?: any[];
  onDistrictCompare?: any[];
  onForSaleCompare?: any[];
  onForRentCompare?: any[];
}

export interface NewBuildListResult {
  results: NewBuildPropertyItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
  message: string;
}

export interface NewBuildSingleResult {
  results: NewBuildPropertyItem | null;
  message?: string;
}

export const getNewBuildProperties = async (data: {
  page: number;
  limit: number;
}): Promise<NewBuildListResult> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/new-build-properties`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return { results: [], pagination: { total: 0, page: 1, limit: 30 }, message: 'error' };
  }
};

export const getNewBuildProperty = async (id: string): Promise<NewBuildSingleResult> => {
  try {
    const response = await axios.get(
      `${baseUrl}/whitelabel/singleNewBuildProperty/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return { results: null, message: 'error' };
  }
};
export const approveSandboxPreview = async (token: string): Promise<{ previewAccepted: string; previewIP: string }> => {
  const response = await axios.post(
    `${sandboxApiBase}/whitelabel/sandbox/approve`,
    { token },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};
